precision highp float;

varying vec2 vUv;

uniform sampler2D tFrom;
uniform sampler2D tTo;
uniform float uProgress;
uniform float uTime;
uniform vec2 uScrollDir;

#define PI 3.14159265

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);

  return mix(
    mix(hash(i),                  hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.22;

  for (int i = 0; i < 3; i++) {
    float n = noise(p);
    v += a * (1.0 - abs(n * 2.4 - 1.2));
    p *= 2.1;
    a *= 0.55;
  }

  return clamp(v, 0.0, 1.0);
}

vec2 safeNormalize(vec2 v) {
  float l = length(v);
  if (l < 0.00001) return vec2(0.0, 1.0);
  return v / l;
}

float cubicInOut(float t) {
  t = clamp(t, 0.0, 1.0);
  return t < 0.5
    ? 4.0 * t * t * t
    : 1.0 - pow(-2.0 * t + 2.0, 3.0) * 0.5;
}

float expoOut(float t) {
  t = clamp(t, 0.0, 1.0);
  return t >= 1.0 ? 1.0 : 1.0 - pow(2.0, -10.0 * t);
}

void main() {
  vec2 uv = vUv;
  vec2 dir = safeNormalize(uScrollDir);

  float p = clamp(uProgress, 0.0, 1.0);
  float smokeFade = pow(sin(p * PI), 0.75);

  float easedP = p;
  vec2 flow = dir * (uTime * 0.15 + easedP * 1.8);

  vec2 warp = vec2(
    fbm(uv * 1.1 + flow + vec2(0.3, 0.7)),
    fbm(uv * 1.4 - flow * 0.4 + vec2(0.8, 0.2))
  );
  vec2 warp2 = vec2(
    fbm(uv * 2.8 - flow * 0.7 + warp * 0.6 + vec2(1.3, 0.4)),
    fbm(uv * 2.5 + flow * 0.3 - warp * 0.4 + vec2(0.2, 1.1))
  );

  float smoke = fbm(uv * 2.2 + warp * 0.4 + warp2 * 0.18 + flow);
  smoke = smoothstep(0.05, 0.65, smoke);
  
  float edgeFade = smoothstep(0.0, 0.06, uv.x) * smoothstep(1.0, 0.94, uv.x)
                 * smoothstep(0.0, 0.06, uv.y) * smoothstep(1.0, 0.94, uv.y);

  float distortionStrength = 0.75 * smokeFade; 
  vec2 offset = dir * smoke * distortionStrength;
  vec2 distortedUV = uv + offset;  

  float glitchPeak = pow(1.0 - abs(p - 0.5) * 2.0, 3.0);
  float scanLine = step(0.5, fract(uv.y * 180.0 + uTime * 12.0));
  float glitchOffset = (hash(vec2(floor(uv.y * 60.0), floor(uTime * 20.0))) - 0.5) * 0.018;

  vec2 glitchedUV = distortedUV + vec2(glitchOffset, 0.0) * glitchPeak * scanLine * 0.6;
  glitchedUV = 1.0 - abs(1.0 - abs(glitchedUV));

  float lumaFrom = dot(texture2D(tFrom, glitchedUV).rgb, vec3(0.2126, 0.7152, 0.0722));
  float ab = (0.06 + lumaFrom * 0.05) * smokeFade * smoke;
  vec2 abOffset = dir * ab;

  vec4 fromCol;
  fromCol.r = texture2D(tFrom, 1.0 - abs(1.0 - abs(glitchedUV + abOffset))).r;
  fromCol.g = texture2D(tFrom, glitchedUV).g;
  fromCol.b = texture2D(tFrom, 1.0 - abs(1.0 - abs(glitchedUV - abOffset))).b;
  fromCol.a = 1.0;

  float lumaTo = dot(texture2D(tTo, glitchedUV).rgb, vec3(0.2126, 0.7152, 0.0722));
  float abTo = (0.06 + lumaTo * 0.05) * smokeFade * smoke;
  vec2 abOffsetTo = dir * abTo;

  vec4 toCol;
  toCol.r = texture2D(tTo, 1.0 - abs(1.0 - abs(glitchedUV + abOffsetTo))).r;
  toCol.g = texture2D(tTo, glitchedUV).g;
  toCol.b = texture2D(tTo, 1.0 - abs(1.0 - abs(glitchedUV - abOffsetTo))).b;
  toCol.a = 1.0;

  float dist = dot(uv - 0.5, dir) + 0.5;
  float maskEdge = 5.0;
  float progressScaled = easedP * (1.0 + maskEdge * 2.0) - maskEdge;
  float combinedMask = dist + (smoke - 2.5) * 0.04;
  float mask = cubicInOut(smoothstep(progressScaled - maskEdge, progressScaled + maskEdge, combinedMask));

  vec4 col = mix(fromCol, toCol, 1.0 - mask);

  vec3 inkColor = vec3(0.01, 0.02, 0.06);
  vec3 inkGlow  = vec3(0.05, 0.12, 0.35);
  float inkMask = pow(smoke, 2.0) * smokeFade * edgeFade;
  
  col.rgb = mix(col.rgb, inkColor, inkMask * 0.38);
  col.rgb += inkGlow * pow(smoke, 18.0) * smokeFade * edgeFade * 0.6;

  float vignette = 1.0 - smoothstep(0.4, 1.4, length((uv - 0.5) * 1.6));
  col.rgb *= mix(1.0, vignette, 0.55 * smokeFade);

  gl_FragColor = vec4(clamp(col.rgb, 0.0, 1.0), 1.0);
}